import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Button,
  HStack,
  Text,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { dataTableButtonStyles } from '@/components/data-table';
import { Divider } from '@/components/divider';
import { toaster } from '@/components/ui/toaster';
import {
  BreadcrumbRoot,
  BreadcrumbLink,
  BreadcrumbCurrentLink,
} from '@/components/ui/breadcrumb';
import { FiCheck, FiChevronRight, FiPlus, FiMapPin } from 'react-icons/fi';
import { useDisclosure } from '@/hooks/use-disclosure';

import { useOrgLocation } from '@/context/organization-location-context';
import { LocationCreateModal } from '@/features/locations/components/location-create-modal';
import { LocationsList } from '@/features/locations/components/location-list';
import { LocationDetailsDrawer } from '@/features/locations/components/location-details-drawer';
import { Location } from '@/features/locations/locations.types';
import { CreateLocationBody } from '@/features/locations/locations.types';

const LocationSelectionPage: React.FC = () => {
  // Get context functions and state
  const { selectLocation, selectedLocation, selectedOrganisation } =
    useOrgLocation();

  // Local state
  const [selectedId, setSelectedId] = useState<string | null>(
    null,
  );
  const [viewLocationId, setViewLocationId] = useState<string | null>(null);

  // Hooks
  const navigate = useNavigate();
  const {
    open: isCreateModalOpen,
    onOpen: onCreateModalOpen,
    onClose: onCreateModalClose,
  } = useDisclosure();
  const {
    open: isDrawerOpen,
    onOpen: onDrawerOpen,
    onClose: onDrawerClose,
  } = useDisclosure();

  // Check if organization is selected, redirect if not
  useEffect(() => {
    if (!selectedOrganisation) {
      navigate('/organisation-selection');
      toaster.create({
        title: 'Organization selection required',
        description: 'Please select an organization first',
        type: 'warning',
        duration: 3000,
      });
    }
  }, [selectedOrganisation, navigate]);

  // Handle location selection
  const handleSelectLocation = (location: Location) => {
    setSelectedId(location.id);
    selectLocation({ id: location.id, name: location.name });
  };

  // Handle location view
  const handleViewLocation = (location: Location) => {
    setViewLocationId(location.id);
    onDrawerOpen();
  };

  // Handle continue button click
  const handleContinue = () => {
    if (selectedId) {
      navigate('/dashboard');
    } else {
      toaster.create({
        title: 'No location selected',
        description: 'Please select a location to continue',
        type: 'warning',
        duration: 3000,
      });
    }
  };

  // Handle back button click
  const handleBack = () => {
    navigate('/organisation-selection');
  };

  // Handle successful location creation
  const handleLocationCreated = (newLocData: CreateLocationBody) => {
    // Create a mock location object with the required properties
    const newLoc: Location = {
      id: Date.now().toString(), // Mock ID
      name: newLocData.name,
      address: newLocData.address,
      email: newLocData.email,
      phone: newLocData.phone,
      // Add other properties as needed
    };

    // Select the newly created location
    handleSelectLocation(newLoc);

    toaster.create({
      title: 'Location created',
      description: `${newLoc.name} has been created and selected`,
      type: 'success',
      duration: 3000,
    });
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setViewLocationId(null);
    onDrawerClose();
  };

  return (
    <Box w="full" h="100%" display="flex" flexDirection="column" p={0}>
      <Box flexShrink={0} p={6} pb={0}>
        {/* Breadcrumb navigation */}
        <BreadcrumbRoot
          separator={<Icon as={FiChevronRight} color="gray.500" />}
          mb={4}
          fontSize="sm"
        >
          <BreadcrumbLink onClick={handleBack} color="blue.500">
            Organization: {selectedOrganisation?.name}
          </BreadcrumbLink>
          <BreadcrumbCurrentLink>Select Location</BreadcrumbCurrentLink>
        </BreadcrumbRoot>
        {/* Header Section - Match AdminPage styling */}
        <Box flexShrink={0} pb={4}>
          <Flex justify="space-between" align="flex-start" mb={6}>
            <Box>
              <HStack gap={3} mb={2}>
                <FiMapPin size={24} color="var(--chakra-colors-blue-500)" />
                <Heading size="lg" color="gray.800">
                  Select Location
                </Heading>
              </HStack>
              <Text color="gray.600" fontSize="md">
                {selectedLocation
                  ? 'You can select a different location if needed.'
                  : 'Please select a location to continue. You will only see locations for your selected organization.'}
              </Text>
              {selectedId && (
                <Text color="blue.600" fontSize="sm" mt={2}>
                  Selected: {selectedLocation?.name || selectedId}
                </Text>
              )}
            </Box>

            <HStack gap={3}>
              <Button
                {...dataTableButtonStyles.outline}
                onClick={onCreateModalOpen}
                disabled={!selectedOrganisation}
                px={6}
              >
                <FiPlus />
                Create Location
              </Button>
              <Button
                {...dataTableButtonStyles.solid}
                disabled={!selectedId}
                onClick={handleContinue}
                px={6}
              >
                <FiCheck />
                Continue
              </Button>
            </HStack>
          </Flex>

          <Divider />
        </Box>
        {/* Locations Table - Takes remaining height */}
        <Box flex="1" minHeight={0} overflow="hidden" p={6} pt={4} h="100%">
          <LocationsList
            onSelect={handleSelectLocation}
            onView={handleViewLocation}
            selectedLocationId={selectedId || undefined}
            organizationId={selectedOrganisation?.id}
          />
        </Box>
      </Box>

      {/* Location Create Modal */}
      <LocationCreateModal
        isOpen={isCreateModalOpen}
        onClose={onCreateModalClose}
        onSuccess={handleLocationCreated}
      />

      {/* Location Details Drawer */}
      <LocationDetailsDrawer
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        locationId={viewLocationId}
      />
    </Box>
  );
};

export default LocationSelectionPage;
